import React, { useState, useEffect, useRef } from "react";
import { TRYON_SERVER_URL } from "../config";
import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../store/AuthActions";
import { fetchUser } from "../Utils/UserUtils";
import { creditsForImage, generateImage, loadGeneratedImage } from "../Utils/ImageGenerateUtils";
import { selectAccessToken, selectAuthenticated, selectUserData } from "../selectors";
import ToastErrorMessage from "../components/ToastErrorMessage";
import Spinner from "../components/Spinner";
import { fetchExperiments } from "../Utils/ExperimentUtils";
import { handleDownload } from "../Utils/DownloadUtils";
import { createVideoGenerationService, videoCredits } from "../Utils/VideoGenerationUtils";
import GradientFont from "../components/GradientFont";
import { useNavigate } from "react-router-dom";
import SignInPrompt from "../components/SignInPrompt";
import Joyride, { STATUS } from "react-joyride";
import { useJoyrideStyles } from "../theme";
import FashionPromptBuilder from "../fashion-prompt-builder/FashionPromptBuilder";
import CanvasLoader from "../components/CanvasLoader";
import { BoxIcon, ImagesIcon, SlidersHorizontalIcon, VideoIcon } from "lucide-react";

export default function Generate({
  selectedPrompt,
  setShowPromptGalleryModal,
  showPromptGalleryModal,
}) {
  const [activeTab, setActiveTab] = useState("Images");
 

  const defaultImageSettings = {
    prompt: selectedPrompt ? selectedPrompt : "",
    resolution: "2K",
    n: 1,
  };

  const defaultVideoSettings = {
    prompt: selectedPrompt ? selectedPrompt : "",
    duration: "8",
    resolution: "1080p",
    aspect_ratio: "16:9",
  };
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [imageSettings, setImageSettings] = useState(defaultImageSettings);
  const [videoSettings, setVideoSettings] = useState(defaultVideoSettings);
  const [videoStillFile, setVideoStillFile] = useState(null);
  const [videoStillPreview, setVideoStillPreview] = useState(null);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [uploadedMediaFiles, setUploadedMediaFiles] = useState([]); // Array of {file, preview, type, name, serverUrl}
  const [showFashionPromptBuilder, setShowFashionPromptBuilder] = useState(false);
  const showFashionPromptBuilderModalRef = useRef(null);
  const accessToken = useSelector(selectAccessToken) || null;
  const authenticated = useSelector(selectAuthenticated);
  const userData = JSON.parse(useSelector(selectUserData) || "null");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorStatus, setErrorStatus] = useState("");
  const [showToastErrorMessage, setShowToastErrorMessage] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  const [sessionImages, setSessionImages] = useState([]);
  const [experimentsFetched, setExperimentsFetched] = useState(false);
  const [experiments, setExperiments] = useState([]);
  const [fetchingExperiments, setFetchingExperiments] = useState({});
  const [pendingOrRunningExperimentIds, setPendingOrRunningExperimentIds] =
    useState([]);
  const [fetchingExperimentsFromServer, setFetchingExperimentsFromServer] =
    useState(false);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const flux_models_mapping = {
    "flux-dev": "FLUX.1-dev",
    "flux-pro": "FLUX.1-pro",
    "flux-pro-1.1": "FLUX.1.1-pro",
    "flux-pro-1.1-ultra": "FLUX.1.1-pro-ultra",
  };
  const imageCredits = creditsForImage(imageSettings.resolution, imageSettings.n);
  const clipCredits = videoCredits(videoSettings.duration, videoSettings.resolution);

  useEffect(() => {
    console.log("accessToken:", accessToken);
    console.log("authenticated:", authenticated);

    if (!experimentsFetched) {
      console.log("fetching experiments");
      if (accessToken && authenticated) {
        fetchExperiments1().then(() => {
          console.log("experiments fetched");
        });
      }
    }
  }, [experiments, accessToken, authenticated]);

  useEffect(() => {
    console.log("selectedPrompt:", selectedPrompt);
    if (selectedPrompt) {
      if (activeTab === "Video") {
        handleSettingChangeVideo("prompt", selectedPrompt);
      } else {
        handleSettingChange("prompt", selectedPrompt);
      }
    }
  }, [selectedPrompt]);

  const fetchExperiments1 = async () => {
    setFetchingExperimentsFromServer(true);
    const response = await fetchExperiments(
      { aimodel: "generate_ai", access: "private" },
      accessToken
    );
    if (response.status === "success") {
      const processedExperiments = response.data.map(exp => {
        if (exp.result && exp.result.image_url && !exp.result.is_video) {
          const url = exp.result.image_url.toLowerCase();
          if (url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov')) {
            return { ...exp, result: { ...exp.result, is_video: true } };
          }
        }
        return exp;
      });
      setExperiments(processedExperiments);
      setExperimentsFetched(true);
      setFetchingExperimentsFromServer(false);
      // add ids of pending or running experiments to the pendingOrRunningExperimentIds array
      let pendingOrRunningExperimentIds = response.data
        .filter(
          (result) => result.status === "pending" || result.status === "running"
        )
        .map((result) => result.id);
      console.log(
        "pending or running experiment ids",
        pendingOrRunningExperimentIds
      );
      setPendingOrRunningExperimentIds(pendingOrRunningExperimentIds);
    } else {
      setFetchingExperimentsFromServer(false);
      console.error("Failed to fetch experiments");
    }
  };

  const handleSettingChange = (setting, value) => {
    if (setting === "n") {
      const count = parseInt(value, 10);
      value = Number.isFinite(count) ? Math.min(4, Math.max(1, count)) : 1;
    }
    if (setting === "resolution" && !["1K", "2K", "4K"].includes(value)) {
      value = "2K";
    }

    setImageSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  const handleSettingChangeVideo = (setting, value) => {
    setVideoSettings((prev) => {
      const next = { ...prev, [setting]: value };
      if ((next.duration === "4" || next.duration === "6") && next.resolution !== "720p") {
        next.resolution = "720p";
      }
      return next;
    });
  };

  const handleVideoStill = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !file.type.startsWith("image/")) {
      setErrorMessage("Choose an image file");
      setErrorStatus("Error");
      setShowToastErrorMessage(true);
      return;
    }
    setVideoStillPreview((current) => {
      if (current) URL.revokeObjectURL(current);
      return URL.createObjectURL(file);
    });
    setVideoStillFile(file);
  };

  const clearVideoStill = () => {
    setVideoStillPreview((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    setVideoStillFile(null);
  };

  const handleGenerate = async () => {
    console.log("Image generation settings: ", imageSettings);

    setIsGenerating(true);
    setShowToastErrorMessage(false);
    setErrorMessage("");
    setErrorStatus("");

    if (!authenticated) {
      setIsGenerating(false);
      navigate("/signin");
      return;
    }

    if (imageSettings.prompt === "") {
      setErrorMessage("Please provide a prompt to generate an image");
      setErrorStatus("Error");
      setShowToastErrorMessage(true);
      setIsGenerating(false);
      return;
    }

    try {
      const referenceFiles = uploadedMediaFiles
        .filter((item) => item.type === "image" && item.file)
        .map((item) => item.file);

      const result = await generateImage({
        prompt: imageSettings.prompt,
        resolution: imageSettings.resolution,
        n: imageSettings.n,
        images: referenceFiles,
        accessToken,
      });

      if (result.status !== "success" || !result.data?.download_url) {
        setErrorMessage(result.message || "Failed to generate image");
        setErrorStatus("Error");
        setShowToastErrorMessage(true);
        setIsGenerating(false);
        return;
      }

      const previewUrl = await loadGeneratedImage(result.data.download_url, accessToken);
      const generated = {
        id: result.data.id,
        prompt: result.data.input_prompt || imageSettings.prompt,
        status: "completed",
        previewUrl,
        resolution: imageSettings.resolution,
        n: imageSettings.n,
        createdAt: result.data.created_at || new Date().toISOString(),
      };
      setSessionImages((prev) => [generated, ...prev]);
      setSelectedGeneration(generated);
      setSelectedExperiment(null);
      setImageSettings((prev) => ({ ...prev, prompt: "" }));

      const user = await fetchUser(accessToken);
      if (user.status === "success") {
        dispatch(setUserData(JSON.stringify(user.data)));
      }
    } catch (error) {
      console.error("Error generating image: ", error);
      setErrorMessage("Failed to generate image: " + error.message);
      setErrorStatus("Error");
      setShowToastErrorMessage(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleMediaUpload = async (files, { persist = true } = {}) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const validFiles = [];
    
    // Validate all files
    for (const file of fileArray) {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");

      if (!isImage && !isVideo) {
        continue; // Skip invalid files
      }

      validFiles.push({
        file,
        type: isImage ? "image" : "video",
        name: file.name,
        preview: URL.createObjectURL(file),
        serverUrl: null,
      });
    }

    if (validFiles.length === 0) {
      setErrorMessage("Please select valid image or video files");
      setErrorStatus("Error");
      setShowToastErrorMessage(true);
      return;
    }

    setIsUploadingVideo(true);
    
    // Add files to state immediately for preview
    setUploadedMediaFiles((prev) => [...prev, ...validFiles]);

    if (persist && authenticated && accessToken) {
      // Upload files in parallel
      const uploadPromises = validFiles.map(async (mediaItem) => {
        try {
          const formData = new FormData();
          formData.append("image", mediaItem.file);
          formData.append("type", mediaItem.type === "image" ? "image_reference" : "video_reference");
          formData.append("access", "private");

          const response = await fetch(`${TRYON_SERVER_URL}/api/v1/experiment_image/`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          });

          if (response.ok) {
            const data = await response.json();
            const mediaUrl = data.image_url || data.url || (data.data && data.data.image_url);
            
            if (mediaUrl) {
              const fullMediaUrl = mediaUrl.startsWith("http") 
                ? mediaUrl 
                : `${TRYON_SERVER_URL}/${mediaUrl}`;
              
              // Update the file in state with server URL
              setUploadedMediaFiles((prev) =>
                prev.map((item) =>
                  item.preview === mediaItem.preview
                    ? { ...item, serverUrl: fullMediaUrl }
                    : item
                )
              );

              // Store first image/video URL in settings
              if (mediaItem.type === "image" && !imageSettings.image_url) {
                handleSettingChange("image_url", mediaUrl);
              } else if (mediaItem.type === "video" && !videoSettings.video_url) {
                handleSettingChangeVideo("video_url", mediaUrl);
              }
            }
          }
        } catch (error) {
          console.error("Error uploading media to server:", error);
        }
      });

      await Promise.all(uploadPromises);
    } else if (persist) {
      // If not authenticated, store local URLs
      const firstImage = validFiles.find((f) => f.type === "image");
      const firstVideo = validFiles.find((f) => f.type === "video");
      
      if (firstImage && !imageSettings.image_url) {
        handleSettingChange("image_url", firstImage.preview);
      }
      if (firstVideo && !videoSettings.video_url) {
        handleSettingChangeVideo("video_url", firstVideo.preview);
      }
    }
    
    setIsUploadingVideo(false);
  };

  const handleRemoveMedia = (previewUrl) => {
    setUploadedMediaFiles((prev) => {
      const itemToRemove = prev.find((item) => item.preview === previewUrl);
      if (itemToRemove) {
        URL.revokeObjectURL(itemToRemove.preview);
      }
      return prev.filter((item) => item.preview !== previewUrl);
    });
  };

  const handleGenerateVideo = async () => {
    console.log("Video generation settings: ", videoSettings);

    if (isGeneratingVideo) return;

    setIsGeneratingVideo(true);
    setShowToastErrorMessage(false);
    setErrorMessage("");
    setErrorStatus("");

    if (!authenticated) {
      setIsGeneratingVideo(false);
      navigate("/signin");
      return;
    }

    if (!String(videoSettings.prompt || "").trim()) {
      setErrorMessage("Enter a prompt to generate a video");
      setErrorStatus("Error");
      setShowToastErrorMessage(true);
      setIsGeneratingVideo(false);
      return;
    }

    const balance = Number(userData?.credits);
    if (!Number.isFinite(balance) || balance < clipCredits) {
      setIsGeneratingVideo(false);
      navigate(`/credits?buy_credits=true&num_of_credits=${clipCredits}`);
      return;
    }

    try {
      const videoService = createVideoGenerationService(accessToken);

      const settings = {
        prompt: videoSettings.prompt,
        duration: videoSettings.duration,
        resolution: videoSettings.resolution,
        aspectRatio: videoSettings.aspect_ratio,
        imageFile: videoStillFile,
      };

      const result = await videoService.generateVideo(settings, (progress) => {
         console.log(progress.message);
      });

      if (result.status === "success") {
        const data = result.data;
        const experimentData = {
            id: data.jobId,
            status: "completed",
            created_on: new Date().toISOString(),
            params: JSON.stringify(settings),
            result: {
                image_url: data.videoUrl,
                is_video: true // Flag to indicate this is a video
            }
        };
        
        setExperiments((prev) => [experimentData, ...prev]);
        setSelectedExperiment(experimentData);
        setSelectedGeneration(null);
        setVideoSettings(defaultVideoSettings);
        clearVideoStill();
        const user = await fetchUser(accessToken);
        if (user.status === "success") {
          dispatch(setUserData(JSON.stringify(user.data)));
        }
      } else {
        throw new Error(result.message);
      }

    } catch (error) {
      console.error("Error generating video: ", error);
      setErrorMessage("Failed to generate video: " + error.message);
      setErrorStatus("Error");
      setShowToastErrorMessage(true);
    } finally {
      setIsGeneratingVideo(false);
    }
  };

  const handleReimagine = (experimentId) => {
    console.log("Reimagining image with experiment id: ", experimentId);

    // open /reimagine with a get parameter for the result image id
    window.location.href = `/reimagineai?id=${experimentId}`;
  };

  const steps = [
    {
      target: ".select_type",
      content:
        "Select the type of data you want to generate. eg. image, video, 3D model, etc.",
      disableBeacon: true,
    },
    {
      target: ".enter_prompt",
      content:
        "Enter a prompt to generate an image/video/3D model. Please describe what you want to see in detail to get the best results.",
      disableBeacon: true,
    },
    {
      target: ".browse_prompt_gallery",
      content: "Click here to browse the prompt gallery",
      disableBeacon: true,
    },
    {
      target: ".select_model",
      content: "Choose a size. 2K is the default.",
      disableBeacon: true,
    },
    {
      target: ".estimated_cost",
      content:
        "Credits update with the size and how many images you ask for.",
      disableBeacon: true,
    },
    {
      target: ".image_count",
      content:
        "Choose how many images to generate, from 1 to 4.",
      disableBeacon: true,
    },
    {
      target: ".generate_button",
      content:
        "Click the generate button to generate an image/video/3D model. The generation will start in a few seconds.",
      disableBeacon: true,
    },
    {
      target: ".generated_images",
      content:
        "See the generated images/videos/3D models. You can click on the image/video/3D model to see the full size.",
      disableBeacon: true,
    },
    {
      target: ".generated_preview",
      content:
        "See the generated preview image/video/3D model. This section shows the generated image/video/3D model in a preview size with the parameters used for generation.",
      disableBeacon: true,
    },
  ];

  const tourStyles = useJoyrideStyles();
  const handleJoyrideCallback = (data) => {
    const { step, status } = data;
    console.log(step, status);

    if ([STATUS.FINISHED].includes(status)) {
      localStorage.setItem("GENERATE_JOYRIDE_STATUS", "finished");
    }
  };

  const tabs = [
    {
      name: "Images",
      icon: <ImagesIcon className="w-5 h-5" />,
      content: (
        <div className="space-y-5 py-2">
          <div className="enter_prompt">
            <label className="mb-3 block font-Mulish text-sm text-graphite">Prompt</label>
            <textarea
              placeholder="Enter your prompt here! You can generate a garment, model, or anything else you can imagine. Please describe what you want to see in detail to get the best results."
              value={imageSettings.prompt}
              onChange={(e) => handleSettingChange("prompt", e.target.value)}
              className="h-28 w-full resize-none rounded-lg border border-hairline bg-white px-3 py-2 font-Mulish text-sm text-secondary outline-none placeholder:text-graphite/60 focus:ring-2 focus:ring-primary-200"
            />

            <button
              onClick={() => showPromptGalleryModal("all")}
              className="browse_prompt_gallery mt-3 inline-flex items-center gap-2 rounded-full border border-primary-200 bg-white px-4 py-2 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
                />
              </svg>
              Browse Prompt Gallery
            </button>
          </div>

          {/* Reference Image Upload */}
          <div className="reference_image">
            <label className="mb-3 block font-Mulish text-sm text-graphite">
              Reference Image (Optional)
            </label>

            <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          const files = e.target.files;
                          if (files && files.length > 0) {
                            handleMediaUpload(files, { persist: false });
                          }
                          e.target.value = "";
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        disabled={isUploadingVideo}
                      />
                      <div className="flex w-full items-center justify-center gap-2 rounded-xl border border-hairline bg-primary-50 px-4 py-3 font-Mulish text-sm text-graphite">
                        {isUploadingVideo ? (
                          <div className="flex items-center gap-2">
                            <Spinner width="4" height="4" fill="fill-primary-700" />
                            <span>Processing...</span>
                          </div>
                        ) : uploadedMediaFiles.filter(f => f.type === "image").length > 0 ? (
                          <div className="flex items-center gap-2 text-primary-700">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                              <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium">{uploadedMediaFiles.filter(f => f.type === "image").length} Image(s) uploaded</span>
                          </div>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 sm:h-5 sm:w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            <span className="font-medium">Upload Images</span>
                          </>
                        )}
                      </div>
                    </div>
            
            {/* Multiple Image Previews - Shows immediately without login */}
            {uploadedMediaFiles.filter(f => f.type === "image").length > 0 && !isUploadingVideo && (
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {uploadedMediaFiles.filter(f => f.type === "image").map((mediaItem, index) => (
                  <div key={index} className="group relative overflow-hidden rounded-lg border border-hairline bg-white">
                    <img
                      src={mediaItem.preview}
                      alt={`Uploaded ${index + 1}`}
                      className="w-full h-24 sm:h-32 object-cover"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveMedia(mediaItem.preview);
                        if (uploadedMediaFiles.filter(f => f.type === "image").length === 1) {
                          handleSettingChange("image_url", "");
                        }
                      }}
                      className="absolute top-1 right-1 rounded-full bg-primary-700 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      title="Remove"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col select_model">
              <label className="mb-3 font-Mulish text-sm text-graphite">Resolution</label>
              <select
                value={imageSettings.resolution}
                onChange={(e) => handleSettingChange("resolution", e.target.value)}
                className="rounded-lg border border-hairline bg-white px-3 py-2 font-Mulish text-sm text-secondary outline-none focus:ring-2 focus:ring-primary-200"
              >
                <option value="1K">1K</option>
                <option value="2K">2K</option>
                <option value="4K">4K</option>
              </select>
            </div>

            <div className="flex flex-col image_count">
              <label className="mb-3 font-Mulish text-sm text-graphite">Images</label>
              <select
                value={imageSettings.n}
                onChange={(e) => handleSettingChange("n", e.target.value)}
                className="rounded-lg border border-hairline bg-white px-3 py-2 font-Mulish text-sm text-secondary outline-none focus:ring-2 focus:ring-primary-200"
              >
                {[1, 2, 3, 4].map((count) => (
                  <option key={count} value={count}>
                    {count}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            className={`generate_button estimated_cost mt-2 w-full rounded-full px-4 py-2.5 font-Mulish font-semibold text-sm ${isGenerating ? "cursor-not-allowed bg-primary-100 text-primary-800" : "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"}`}
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : `Generate (${imageCredits} credits)`}
          </button>
        </div>
      ),
    },
    {
      name: "Video",
      icon: <VideoIcon className="w-5 h-5" />,
      content: (
        <div className="flex w-full flex-col">
          <div className="flex flex-col lg:h-full lg:overflow-hidden">
            <div className="flex-1 lg:overflow-y-auto select_type ">
              <div className="">
                <div className="space-y-4 sm:space-y-5 py-2 pt-0">
                  {/* Prompt Input */}
                  <div className="enter_prompt">
                    <label className="mb-2 block font-Mulish font-semibold text-lg text-secondary">
                      Prompt
                    </label>
                    <textarea
                      placeholder="Describe the scene, the motion, and the camera."
                      value={videoSettings.prompt}
                      onChange={(e) => handleSettingChangeVideo("prompt", e.target.value)}
                      className="h-24 w-full resize-none rounded-lg border border-hairline bg-white px-3 py-2 font-Mulish text-sm text-secondary outline-none focus:ring-2 focus:ring-primary-200"
                    />
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                      <button
                        type="button"
                        onClick={() => showPromptGalleryModal("all")}
                        className="inline-flex flex-1 items-center justify-center rounded-full border border-primary-200 bg-white px-4 py-2.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                      >
                        Prompt Gallery
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowFashionPromptBuilder(true)}
                        className="inline-flex flex-1 items-center justify-center rounded-full border border-primary-200 bg-white px-4 py-2.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                      >
                        Prompt Builder
                      </button>
                    </div>
                  </div>

                  <div>
                    <h2 className="mb-2 font-Mulish font-semibold text-lg text-secondary">Starting photo</h2>
                    <p className="mb-3 font-Mulish text-sm text-graphite">Optional. The clip starts from this photo.</p>
                    {videoStillPreview ? (
                      <div className="relative rounded-2xl bg-primary-50 p-2">
                        <div className="flex h-32 items-center justify-center overflow-hidden rounded-lg bg-white">
                          <img src={videoStillPreview} alt="Starting photo" className="max-h-full max-w-full object-contain" />
                        </div>
                        <button
                          type="button"
                          onClick={clearVideoStill}
                          className="absolute right-4 top-4 rounded-full border border-hairline bg-white p-2"
                          aria-label="Remove photo"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => document.getElementById("video-still").click()}
                        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-50 px-4 py-8 font-Mulish text-sm text-graphite hover:bg-primary-100"
                      >
                        Upload a photo
                      </button>
                    )}
                    <input id="video-still" type="file" accept="image/*" onChange={handleVideoStill} className="hidden" />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="flex flex-col">
                      <label className="mb-2 font-Mulish text-sm text-graphite">Length</label>
                      <select
                        value={videoSettings.duration}
                        onChange={(e) => handleSettingChangeVideo("duration", e.target.value)}
                        className="rounded-lg border border-hairline bg-white px-3 py-2 font-Mulish text-sm text-secondary outline-none focus:ring-2 focus:ring-primary-200"
                      >
                        <option value="4">4 seconds</option>
                        <option value="6">6 seconds</option>
                        <option value="8">8 seconds</option>
                      </select>
                    </div>
                    <div className="flex flex-col">
                      <label className="mb-2 font-Mulish text-sm text-graphite">Size</label>
                      <select
                        value={videoSettings.resolution}
                        onChange={(e) => handleSettingChangeVideo("resolution", e.target.value)}
                        className="rounded-lg border border-hairline bg-white px-3 py-2 font-Mulish text-sm text-secondary outline-none focus:ring-2 focus:ring-primary-200"
                      >
                        <option value="720p">720p</option>
                        <option value="1080p" disabled={videoSettings.duration !== "8"}>1080p</option>
                        <option value="4k" disabled={videoSettings.duration !== "8"}>4K</option>
                      </select>
                    </div>
                    <div className="flex flex-col">
                      <label className="mb-2 font-Mulish text-sm text-graphite">Frame</label>
                      <select
                        value={videoSettings.aspect_ratio}
                        onChange={(e) => handleSettingChangeVideo("aspect_ratio", e.target.value)}
                        className="rounded-lg border border-hairline bg-white px-3 py-2 font-Mulish text-sm text-secondary outline-none focus:ring-2 focus:ring-primary-200"
                      >
                        <option value="16:9">Landscape</option>
                        <option value="9:16">Portrait</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white pt-2">
              <button
                type="button"
                className={`generate_button w-full rounded-full px-4 py-2.5 font-Mulish font-semibold text-sm ${isGeneratingVideo ? "cursor-not-allowed bg-primary-100 text-primary-800" : "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"}`}
                onClick={handleGenerateVideo}
                disabled={isGeneratingVideo}
              >
                {isGeneratingVideo ? "Generating..." : `Generate (${clipCredits} credits)`}
              </button>
            </div>
          </div>
        </div>
      ),
    },
    {
      name: "3D Models",
      icon: <BoxIcon className="w-5 h-5" />,
      content: (
        <div className="flex flex-col items-center justify-center rounded-xl bg-primary-50 px-6 py-10">
          <div className="mb-4 h-10 w-10 text-primary-700">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-full h-full"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-center font-Mulish font-semibold text-lg text-secondary">
            3D models are on the way
          </h3>
          <p className="mb-4 max-w-md text-center font-Mulish text-sm text-graphite">
            A text prompt will be enough to make a 3D asset. This tab is not ready yet.
          </p>
          <div className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-white px-4 py-2 font-Mulish text-sm text-primary-700">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
              />
            </svg>
            Get notified when it launches
          </div>
        </div>
      ),
    },
  ];

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleErrorClose = () => {
    setShowToastErrorMessage(false);
    setErrorMessage("");
    setErrorStatus("");
  };

  return (
    <>
      {localStorage.getItem("GENERATE_JOYRIDE_STATUS") !== "finished" ? (
        <Joyride
          callback={handleJoyrideCallback}
          steps={steps} styles={tourStyles}
          showProgress={true}
          showSkipButton={true}
          continuous={true}
        />
      ) : (
        <></>
      )}
      <div className="grid h-[calc(100vh-5rem)] w-full grid-cols-1 gap-4 overflow-y-auto bg-ground p-4 selection:bg-primary-100 lg:grid-cols-12 lg:overflow-hidden lg:min-h-0">
        <div className="flex min-h-[28rem] flex-col rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)] lg:col-span-4 lg:h-full lg:min-h-0 lg:overflow-hidden">
          <div className="flex h-full min-h-0 flex-col">
            <div className="select_type flex-shrink-0 px-4 pt-4">
              <ul className="flex gap-6 border-b border-hairline">
                {tabs.map((tab) => (
                  <li
                    key={tab.name}
                    className={`-mb-px cursor-pointer border-b-2 pb-2 ${
                      activeTab === tab.name
                        ? "border-primary-500"
                        : "border-transparent"
                    }`}
                    onClick={() => handleTabClick(tab.name)}
                  >
                    <a
                      href="#"
                      className={`inline-flex items-center gap-2 ${
                        activeTab === tab.name
                          ? "font-Mulish font-semibold text-secondary"
                          : "font-Mulish text-graphite hover:text-secondary"
                      }`}
                    >
                      {tab.icon}
                      <span className="whitespace-nowrap">{tab.name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="text-medium text-gray-500 dark:text-gray-400 rounded-lg">
                {tabs.find((tab) => tab.name === activeTab)?.content}
              </div>
            </div>
          </div>
        </div>

        {/* Middle Column */}
        <div className="generated_preview flex min-h-[28rem] flex-col rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)] lg:col-span-5 lg:h-full lg:min-h-0 lg:overflow-hidden">
          <div className="flex h-full min-h-0 flex-col">
            <h2 className="mb-3 shrink-0 font-Mulish font-semibold text-lg text-secondary">Result</h2>
            <div className="flex min-h-[320px] flex-1 items-center justify-center overflow-hidden rounded-2xl bg-primary-50 p-4 lg:min-h-0">
              {isGenerating || isGeneratingVideo ? (
                <CanvasLoader title={`Generating ${isGeneratingVideo ? "video" : "image"}`} />
              ) : selectedGeneration ? (
                <div className="w-full h-full flex items-center justify-center relative">
                  <img
                    src={selectedGeneration.previewUrl}
                    alt="Generated"
                    className="max-h-full max-w-full rounded-xl object-contain"
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      className="rounded-full border border-hairline bg-white p-2 hover:bg-primary-50"
                      onClick={() => handleDownload(selectedGeneration.previewUrl)}
                      title="Download image"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="h-4 w-4 text-primary-700"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 16.5V21h18v-4.5M12 3v12m0 0l-3-3m3 3l3-3"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ) : selectedExperiment ? (
                selectedExperiment.status === "completed" ? (
                  <div className="w-full h-full flex items-center justify-center relative">
                    {selectedExperiment.result.is_video ? (
                      <video
                        src={
                          selectedExperiment.result.image_url.startsWith("http") || 
                          selectedExperiment.result.image_url.startsWith("blob:")
                            ? selectedExperiment.result.image_url
                            : `${TRYON_SERVER_URL}/${selectedExperiment.result.image_url}`
                        }
                        controls
                        loop
                        muted
                        playsInline
                        className="max-h-full max-w-full rounded-xl object-contain"
                      />
                    ) : (
                      <img
                        src={`${TRYON_SERVER_URL}/${selectedExperiment.result.image_url}`}
                        alt="Generated Image"
                        className="max-h-full max-w-full rounded-xl object-contain"
                      />
                    )}
                    <div className="absolute top-2 right-2 flex gap-2">
                      <button
                        className="rounded-full border border-hairline bg-white p-2 hover:bg-primary-50"
                        onClick={() =>
                          handleDownload(
                            selectedExperiment.result.is_video &&
                              (selectedExperiment.result.image_url.startsWith(
                                "http"
                              ) || selectedExperiment.result.image_url.startsWith("blob:"))
                              ? selectedExperiment.result.image_url
                              : `${TRYON_SERVER_URL}/${selectedExperiment.result.image_url}`
                          )
                        }
                        title={
                          selectedExperiment.result.is_video
                            ? "Download video"
                            : "Download image"
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="h-4 w-4 text-primary-700"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 16.5V21h18v-4.5M12 3v12m0 0l-3-3m3 3l3-3"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Reimagine button with improved styling */}
                    {!selectedExperiment.result.is_video && (
                    <button
                      className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-primary-200 bg-white px-5 py-2 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                      onClick={() =>
                        handleReimagine(selectedExperiment.result.id)
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                        />
                      </svg>
                      Reimagine
                    </button>
                    )}
                  </div>
                ) : selectedExperiment.status === "failed" ? (
                  <div className="text-center space-y-3">
                    <div className="w-16 h-16 mx-auto text-red-500">
                      <svg
                        className="w-full h-full"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                    <p className="font-Mulish font-semibold text-secondary">
                      Generation failed
                    </p>
                    <p className="font-Mulish text-sm text-graphite">
                      Try again with a different prompt
                    </p>
                  </div>
                ) : (
                  <CanvasLoader title="Generating image" />
                )
              ) : fetchingExperimentsFromServer || (authenticated && accessToken && !experimentsFetched) ? (
                <CanvasLoader title="Loading your images" />
              ) : (
                <div className="space-y-2 px-4 text-center">
                  <div className="mx-auto h-10 w-10 text-primary-700">
                    <svg
                      className="h-full w-full"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <p className="font-Mulish font-semibold text-lg text-secondary">{activeTab === "Video" ? "No video yet" : "No image yet"}</p>
                  <p className="font-Mulish text-sm text-graphite">
                    {activeTab === "Video" ? "Your video will show up here" : "Your image will show up here"}
                  </p>
                </div>
              )}
            </div>

            {/* Image info and parameters at bottom */}
            {selectedGeneration && (
              <div className="mt-3 shrink-0 border-t border-hairline pt-3 font-Mulish">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-2 py-0.5 text-xs rounded-full bg-green-50 text-green-600">
                    Completed
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(selectedGeneration.createdAt).toLocaleString()}
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Prompt</label>
                    <p className="text-sm text-gray-700 line-clamp-2">{selectedGeneration.prompt}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-gray-500">Resolution</span>
                      <p className="font-medium text-gray-700 mt-0.5">{selectedGeneration.resolution}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Images</span>
                      <p className="font-medium text-gray-700 mt-0.5">{selectedGeneration.n}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {selectedExperiment && !selectedGeneration && (
              <div className="mt-3 shrink-0 border-t border-hairline pt-3 font-Mulish">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">
                      Status
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${
                        selectedExperiment.status === "completed"
                          ? "bg-green-50 text-green-600"
                          : selectedExperiment.status === "failed"
                          ? "bg-red-50 text-red-600"
                          : "bg-yellow-50 text-yellow-600"
                      }`}
                    >
                      {selectedExperiment.status.charAt(0).toUpperCase() +
                        selectedExperiment.status.slice(1)}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {new Date(selectedExperiment.created_on).toLocaleString()}
                  </span>
                </div>

                {/* Parameters Grid */}
                <div className="space-y-3">
                  {/* Prompt */}
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">
                      Prompt
                    </label>
                    <p className="text-sm text-gray-700 line-clamp-2">
                      {JSON.parse(selectedExperiment.params).prompt}
                    </p>
                  </div>

                  {/* Parameters Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    {/* Model - Always show */}
                    <div>
                      <span className="text-gray-500">Model</span>
                      <p className="font-medium text-gray-700 mt-0.5">
                        {flux_models_mapping[
                          JSON.parse(selectedExperiment.params).model
                        ] || JSON.parse(selectedExperiment.params).model}
                      </p>
                    </div>

                    {/* Dynamically show parameters based on what's in the experiment params */}
                    {Object.entries(JSON.parse(selectedExperiment.params)).map(
                      ([key, value]) => {
                        // Skip model and prompt as they're handled separately
                        if (key === "model" || key === "prompt") return null;

                        // Format the display based on parameter type
                        let displayValue = value;
                        let displayName = key
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase());

                        // Special formatting for certain parameter types
                        if (
                          key === "width" &&
                          JSON.parse(selectedExperiment.params).height
                        ) {
                          return (
                            <div key={key}>
                              <span className="text-gray-500">Dimensions</span>
                              <p className="font-medium text-gray-700 mt-0.5">
                                {value} ×{" "}
                                {JSON.parse(selectedExperiment.params).height}
                              </p>
                            </div>
                          );
                        } else if (key === "height") {
                          // Skip height as it's handled with width
                          return null;
                        } else if (typeof value === "boolean") {
                          displayValue = value ? "Enabled" : "Disabled";
                        }

                        return (
                          <div
                            key={key}
                            className={
                              key.length > 12 ? "col-span-2 md:col-span-1" : ""
                            }
                          >
                            <span className="text-gray-500">{displayName}</span>
                            <p className="font-medium text-gray-700 mt-0.5">
                              {displayValue}
                            </p>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="generated_images flex min-h-[20rem] flex-col rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)] lg:col-span-3 lg:h-full lg:min-h-0">
          <div className="mb-3 flex flex-shrink-0 items-baseline justify-between">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-Mulish font-semibold text-lg text-secondary">
                Generated images
              </h2>
              <span className="font-Mulish text-sm text-graphite">
                {sessionImages.length + experiments.filter((exp) => exp.status === "completed").length}{" "}
                results
              </span>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            {authenticated === null ? (
              <div className="flex h-full items-center justify-center rounded-xl bg-primary-50 px-4">
                <CanvasLoader title="Loading your images" />
              </div>
            ) : authenticated ? (
              <div className="h-full overflow-y-auto pr-2 custom-scrollbar">
                {sessionImages.length > 0 || (experimentsFetched && experiments.length > 0) ? (
                  <div className="grid grid-cols-2 gap-3">
                    {sessionImages.map((image) => (
                      <div
                        key={image.id}
                        onClick={() => {
                          setSelectedGeneration(image);
                          setSelectedExperiment(null);
                        }}
                        className={`relative group transition-all duration-200 cursor-pointer ${
                          selectedGeneration?.id === image.id
                            ? "border-2 border-primary-500"
                            : "border border-hairline hover:border-primary-200"
                        } rounded-lg overflow-hidden`}
                      >
                        <div className="relative aspect-square w-full overflow-hidden">
                          <img
                            src={image.previewUrl}
                            alt="Generated"
                            className="w-full h-full object-cover object-top transition-transform duration-200 group-hover:scale-105"
                          />
                        </div>
                      </div>
                    ))}
                    {experiments.map(
                      (experiment) =>
                        experiment.status === "completed" && (
                          <div
                            key={experiment.id}
                            onClick={() => {
                              setSelectedExperiment(experiment);
                              setSelectedGeneration(null);
                            }}
                            className={`relative group transition-all duration-200 cursor-pointer ${
                              selectedExperiment?.id === experiment.id
                                ? "border-2 border-primary-500"
                                : "border border-hairline hover:border-primary-200"
                            } rounded-lg overflow-hidden`}
                          >
                            <div className="relative aspect-square w-full overflow-hidden">
                              {experiment.result && (
                                <>
                                  {experiment.result.is_video ? (
                                    <div className="w-full h-full bg-gray-100 flex items-center justify-center relative">
                                      <video
                                        src={
                                          experiment.result.image_url.startsWith("http") || 
                                          experiment.result.image_url.startsWith("blob:")
                                            ? experiment.result.image_url
                                            : `${TRYON_SERVER_URL}/${experiment.result.image_url}`
                                        }
                                        className="w-full h-full object-cover"
                                        muted
                                        playsInline
                                        loop
                                        onMouseOver={e => e.target.play()}
                                        onMouseOut={e => e.target.pause()}
                                      />
                                      <div className="absolute top-2 right-2 bg-black/50 rounded-full p-1 z-10">
                                        <VideoIcon className="w-4 h-4 text-white" />
                                      </div>
                                    </div>
                                  ) : (
                                    <img
                                      src={`${TRYON_SERVER_URL}/${experiment.result.image_url}`}
                                      alt="Generated Image"
                                      className="w-full h-full object-cover object-top transition-transform duration-200 group-hover:scale-105"
                                    />
                                  )}
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                                      <p className="text-white text-xs truncate px-1">
                                        {JSON.parse(experiment.params).prompt ||
                                          "No prompt available"}
                                      </p>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )
                    )}
                  </div>
                ) : fetchingExperimentsFromServer || (accessToken && !experimentsFetched) ? (
                  <div className="flex h-full items-center justify-center rounded-xl bg-primary-50 px-4">
                    <CanvasLoader title="Loading your images" />
                  </div>
                ) : (
                  <div className="flex h-full flex-col items-center justify-center rounded-xl bg-primary-50 px-4 text-center">
                    <div className="mb-2 h-10 w-10 text-primary-700">
                      <svg
                        className="h-full w-full"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                      </svg>
                    </div>
                    <p className="font-Mulish font-semibold text-lg text-secondary">
                      No images yet
                    </p>
                    <p className="mt-1 font-Mulish text-sm text-graphite">
                      Generated images will show up here
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <SignInPrompt
                title="Sign in to see your images"
                message="Generated images will show up here."
                onSignIn={() => navigate("/signin")}
                className="h-full px-4 py-8"
              />
            )}
          </div>

          <style jsx>{`
            .custom-scrollbar::-webkit-scrollbar {
              width: 5px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: transparent;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #f43f5e40;
              border-radius: 20px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #f43f5e80;
            }
          `}</style>
        </div>

        {showToastErrorMessage && (
          <ToastErrorMessage
            errorMessage={errorMessage}
            errorStatus={errorStatus}
            handleErrorClose={handleErrorClose}
          />
        )}
        {showFashionPromptBuilder && (
          <FashionPromptBuilder
            isModal={true}
            setShowFashionPromptBuilder={setShowFashionPromptBuilder}
            onSelectPrompt={(prompt) => {
              if (activeTab === "Video") {
                handleSettingChangeVideo("prompt", prompt);
              } else {
                handleSettingChange("prompt", prompt);
              }
            }}
            showFashionPromptBuilderRef={showFashionPromptBuilderModalRef}
          />
        )}
      </div>
    </>
  );
}
